// Skill Registry API: fetch wrappers over the house createApiClient() helper,
// one per route in plan giggly-tinkering-plum.md §4. Same one-off
// createApiClient()-per-call pattern as entities/agent/api/toolsApi.ts and
// entities/thread/model/useShareThread.ts — no context-injected client, no
// generated OpenAPI client, since this is a small, self-contained surface.
//
// CONTRACT NOTE — verified directly against the backend's landed code
// (src/agenthub/schemas/skills.py, routes/skills.py, routes/admin/skills.py,
// services/skills/skill_service.py) as of this pass, not just the plan doc:
// - `createSkill`/`publishSkillVersion` send plain JSON (`SkillCreateRequest`/
//   `SkillVersionCreateRequest`), NOT multipart. The backend does not accept
//   asset file uploads on either route today — `create_skill`/`_write_version`
//   only ever write `skill_md_content` to disk. The create/edit-skill UI has
//   no file input for this reason; add one only once the backend exposes a
//   way to upload assets.
// - `SkillCreateRequest` requires a client-supplied `slug` (pattern
//   `^[a-z0-9][a-z0-9-]*$`) — it is not server-generated from `name`.
// - Version history is explicit (`/skills/{id}/versions` and the owner
//   `/owner/agents/{agent_id}/skills/{skill_id}/versions` mirror). Inactive
//   versions are drafts; activation copies the selected version into a new
//   active row, matching the owner prompt lifecycle.
// - Registry attachments are direct in V1. Legacy approval endpoints remain
//   backend-only for compatibility with old rows; the frontend no longer
//   exposes an approval queue.

import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import type { Skill, SkillAttachment, SkillAttachmentScope, SkillDetail, SkillShare, SkillSharePermission, SkillVersion } from '@/entities/skill/model/types';
import {
  toAgentSkillList,
  toSkill,
  toSkillAttachment,
  toSkillDetail,
  toSkillList,
  toSkillShare,
  toSkillVersion,
} from '@/entities/skill/model/toSkillView';
import type { AgentSkillList } from '@/entities/skill/model/types';

function getClient() {
  return createApiClient({
    baseUrl: readEnv('VITE_API_BASE_URL') ?? '',
    auth: { getToken },
  });
}

const BASE = '/api/v1/skills';
const OWNER_AGENT_BASE = '/api/v1/owner/agents';

function skillPath(skillId: string): string {
  return `${BASE}/${encodeURIComponent(skillId)}`;
}

// --- Skills ---

export async function listSkills(): Promise<Skill[]> {
  const raw = await getClient().request({ path: BASE });
  return toSkillList(raw);
}

export async function getSkill(skillId: string): Promise<SkillDetail> {
  const raw = await getClient().request({ path: skillPath(skillId) });
  const detail = toSkillDetail(raw);
  if (detail === null) {
    throw new Error(`Skill ${skillId} returned an unexpected payload`);
  }
  return detail;
}

export type CreateSkillInput = {
  name: string;
  slug: string;
  description: string;
  skillMdContent: string;
};

export async function createSkill(input: CreateSkillInput): Promise<Skill> {
  const raw = await getClient().request({
    method: 'POST',
    path: BASE,
    body: {
      name: input.name,
      slug: input.slug,
      description: input.description,
      skill_md_content: input.skillMdContent,
    },
  });
  const skill = toSkill(raw);
  if (skill === null) throw new Error('createSkill returned an unexpected payload');
  return skill;
}

export type PublishVersionInput = {
  skillMdContent: string;
  changeSummary?: string;
};

export async function publishSkillVersion(
  skillId: string,
  input: PublishVersionInput,
): Promise<SkillVersion> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${skillPath(skillId)}/versions`,
    body: {
      skill_md_content: input.skillMdContent,
      change_summary: input.changeSummary ?? null,
    },
  });
  const version = toSkillVersion(raw);
  if (version === null) throw new Error('publishSkillVersion returned an unexpected payload');
  return version;
}

export async function listSkillVersions(skillId: string): Promise<SkillVersion[]> {
  const raw = await getClient().request({ path: `${skillPath(skillId)}/versions` });
  const versions = raw && typeof raw === 'object' && 'versions' in raw
    ? (raw as { versions?: unknown }).versions
    : raw;
  return Array.isArray(versions)
    ? versions.map(toSkillVersion).filter((version): version is SkillVersion => version !== null)
    : [];
}

export async function activateSkillVersion(
  skillId: string,
  versionId: string,
): Promise<SkillVersion> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${skillPath(skillId)}/versions/${encodeURIComponent(versionId)}/activate`,
  });
  const version = toSkillVersion(raw);
  if (version === null) throw new Error('activateSkillVersion returned an unexpected payload');
  return version;
}

export async function deleteSkillVersion(skillId: string, versionId: string): Promise<void> {
  await getClient().request({
    method: 'DELETE',
    path: `${skillPath(skillId)}/versions/${encodeURIComponent(versionId)}`,
  });
}

export async function rollbackSkill(skillId: string, sourceVersionId: string): Promise<SkillVersion> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${skillPath(skillId)}/rollback`,
    body: { source_version_id: sourceVersionId },
  });
  const version = toSkillVersion(raw);
  if (version === null) throw new Error('rollbackSkill returned an unexpected payload');
  return version;
}

// --- Attachments ---

export type AttachSkillInput = {
  scope: SkillAttachmentScope;
  agentId?: string;
};

export async function attachSkillToScope(
  skillId: string,
  input: AttachSkillInput,
): Promise<SkillAttachment> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${skillPath(skillId)}/attachments`,
    body: { scope: input.scope, ...(input.agentId ? { agent_id: input.agentId } : {}) },
  });
  const attachment = toSkillAttachment(raw);
  if (attachment === null) throw new Error('attachSkillToScope returned an unexpected payload');
  return attachment;
}

export async function listAgentSkills(agentId: string): Promise<AgentSkillList> {
  const raw = await getClient().request({
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills`,
  });
  return toAgentSkillList(raw);
}

export async function attachAgentSkill(
  agentId: string,
  skillId: string,
): Promise<SkillAttachment> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/attachment`,
  });
  const attachment = toSkillAttachment(raw);
  if (attachment === null) {
    throw new Error('attachAgentSkill returned an unexpected payload');
  }
  return attachment;
}

export async function listAgentSkillVersions(
  agentId: string,
  skillId: string,
): Promise<SkillVersion[]> {
  const raw = await getClient().request({
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/versions`,
  });
  const versions = raw && typeof raw === 'object' && 'versions' in raw
    ? (raw as { versions?: unknown }).versions
    : raw;
  return Array.isArray(versions)
    ? versions.map(toSkillVersion).filter((version): version is SkillVersion => version !== null)
    : [];
}

export async function publishAgentSkillVersion(
  agentId: string,
  skillId: string,
  input: PublishVersionInput & { activate?: boolean },
): Promise<SkillVersion> {
  const params = input.activate === false ? '?activate=false' : '';
  const raw = await getClient().request({
    method: 'POST',
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/versions${params}`,
    body: {
      skill_md_content: input.skillMdContent,
      change_summary: input.changeSummary ?? null,
    },
  });
  const version = toSkillVersion(raw);
  if (version === null) throw new Error('publishAgentSkillVersion returned an unexpected payload');
  return version;
}

export async function activateAgentSkillVersion(
  agentId: string,
  skillId: string,
  versionId: string,
): Promise<SkillVersion> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/activate`,
  });
  const version = toSkillVersion(raw);
  if (version === null) throw new Error('activateAgentSkillVersion returned an unexpected payload');
  return version;
}

export async function deleteAgentSkillVersion(
  agentId: string,
  skillId: string,
  versionId: string,
): Promise<void> {
  await getClient().request({
    method: 'DELETE',
    path: `${OWNER_AGENT_BASE}/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}`,
  });
}

// --- Shares ---

export type ShareSkillInput = {
  userId: string;
  permission: SkillSharePermission;
};

export async function shareSkill(skillId: string, input: ShareSkillInput): Promise<SkillShare> {
  const raw = await getClient().request({
    method: 'POST',
    path: `${skillPath(skillId)}/shares`,
    body: { user_id: input.userId, permission: input.permission },
  });
  const share = toSkillShare(raw);
  if (share === null) throw new Error('shareSkill returned an unexpected payload');
  return share;
}

export async function unshareSkill(skillId: string, userId: string): Promise<void> {
  await getClient().request({
    method: 'DELETE',
    path: `${skillPath(skillId)}/shares/${encodeURIComponent(userId)}`,
  });
}
